import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Footer from '@/components/Footer';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import * as publicBlogApi from '@/api/publicBlogApi';
import * as blogApi from '@/api/blogApi';
import { Blog, BlogCategory } from '@/types/blog';
import { format } from 'date-fns';
import ProvenResults from '@/components/shared/ProvenResults';
import SEO from '@/components/shared/SEO';
import { useAuth } from '@/components/auth/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface BlogPostProps {
  useIdInsteadOfSlug?: boolean;
}

const BlogPost: React.FC<BlogPostProps> = ({ useIdInsteadOfSlug = false }) => {
  const params = useParams();
  const { slug, id } = params;
  const location = useLocation();
  const [blog, setBlog] = useState<Blog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDraft, setIsDraft] = useState(false);
  const { user } = useAuth();

  // Extract ID from URL if not in params and we're in preview mode
  const extractedId = useMemo(() => {
    if (useIdInsteadOfSlug && !id) {
      const pathParts = location.pathname.split('/');
      const lastPart = pathParts[pathParts.length - 1];
      console.log("Extracted ID from URL:", lastPart);
      return lastPart;
    }
    return id;
  }, [useIdInsteadOfSlug, id, location.pathname]);

  // Add component mount logging
  useEffect(() => {
    console.log("BlogPost component mounted");
    console.log("Props:", { useIdInsteadOfSlug });
    console.log("URL params:", params);
    console.log("Parsed params:", { slug, id });
    console.log("Final ID to use:", useIdInsteadOfSlug ? extractedId : slug);
    console.log("Location:", location);
    console.log("User authenticated:", !!user);
  }, [useIdInsteadOfSlug, slug, id, extractedId, user, params, location]);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        
        // Determine if we're using ID or slug for lookup
        const blogIdentifier = useIdInsteadOfSlug ? extractedId : slug;
        
        if (!blogIdentifier) {
          setError('Blog post not found');
          return;
        }

        console.log(`BlogPost: Attempting to fetch blog with ${useIdInsteadOfSlug ? 'ID' : 'slug'}: ${blogIdentifier}`);
        console.log('BlogPost: Authentication status:', user ? 'Authenticated' : 'Not authenticated');
        
        let response;
        
        // When using ID, we bypass most of the complex logic since we're already authenticated
        // and have direct access to the blog post
        if (useIdInsteadOfSlug) {
          try {
            console.log('BlogPost: Fetching by ID directly');
            response = await blogApi.getBlogById(blogIdentifier);
            console.log('BlogPost: Blog fetched by ID:', response);
            
            if (response && response.status === 'draft') {
              console.log('BlogPost: Draft post detected');
              setIsDraft(true);
            }
          } catch (err) {
            console.error('BlogPost: Error fetching blog by ID:', err);
            setError('Failed to load blog post');
            return;
          }
        } else {
          // Debug permissions first
          try {
            const permissionCheck = await blogApi.checkBlogPermissions(blogIdentifier as string);
            console.log('BlogPost: Permission check result:', permissionCheck);
            
            // If we can't access the blog with either method, show error early
            if (!permissionCheck.publicAccess && !permissionCheck.authAccess) {
              console.error('BlogPost: No access to blog with either method');
              setError('Blog post not found');
              setLoading(false);
              return;
            }
            
            // If this is a draft post, verify we have auth access
            if (permissionCheck.authData?.status === 'draft' && !permissionCheck.authAccess) {
              console.error('BlogPost: Cannot access draft post without auth');
              setError('You need to be logged in to view draft posts');
              setLoading(false);
              return;
            }
          } catch (permError) {
            console.error('BlogPost: Error checking permissions:', permError);
          }
          
          // Check if user is authenticated and ensure token is valid
          let isAuthenticated = false;
          if (user) {
            try {
              // Ensure we have a valid session
              const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
              if (sessionError) {
                console.error('BlogPost: Session error:', sessionError);
                isAuthenticated = false;
              } else if (sessionData?.session) {
                console.log('BlogPost: Valid session found');
                
                // Refresh token to ensure it's valid
                const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
                if (refreshError) {
                  console.error('BlogPost: Token refresh error:', refreshError);
                  isAuthenticated = false;
                } else if (refreshData?.session) {
                  console.log('BlogPost: Session refreshed successfully');
                  isAuthenticated = true;
                }
              }
            } catch (authError) {
              console.error('BlogPost: Auth error:', authError);
              isAuthenticated = false;
            }
          }
          
          // This allows viewing draft posts during editing
          if (isAuthenticated) {
            console.log('BlogPost: User is authenticated, trying to fetch with admin API');
            try {
              console.log('BlogPost: Calling blogApi.getBlogBySlug');
              response = await blogApi.getBlogBySlug(blogIdentifier as string);
              console.log('BlogPost: Admin API response:', response);
              
              // If we found a draft post, flag it
              if (response && response.status === 'draft') {
                console.log('BlogPost: Draft post detected');
                setIsDraft(true);
              }
            } catch (err) {
              console.warn('BlogPost: Authenticated fetch failed:', err);
              // Fall back to public API if there's an error
              console.log('BlogPost: Falling back to public API');
              response = await publicBlogApi.getPublicBlogBySlug(blogIdentifier as string);
            }
          } else {
            // For unauthenticated users, only show published posts
            console.log('BlogPost: User is not authenticated, using public API');
            response = await publicBlogApi.getPublicBlogBySlug(blogIdentifier as string);
          }
        }
        
        console.log('BlogPost: API response:', response);
        
        // Handle potential response formats
        let blogData;
        if (!response) {
          console.error('BlogPost: No response received');
          setError('Blog post not found');
          return;
        } else if (response.note && response.note.includes('schema relationship issues')) {
          // This is from the Edge Function with fallback mode due to relationship issues
          blogData = {
            ...response,
            categories: [], // No categories in fallback mode
            author: null    // No author in fallback mode
          };
          console.warn('BlogPost: Blog loaded with limited data due to schema issues');
        } else {
          // Normal response
          blogData = response;
          // Ensure categories is always an array
          blogData.categories = blogData.categories || [];
        }

        console.log('BlogPost: Final blog data:', blogData);
        setBlog(blogData);
      } catch (error) {
        console.error('BlogPost: Error fetching blog:', error);
        setError('Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [slug, id, user, useIdInsteadOfSlug, extractedId, location]);

  // Create article structured data
  const articleSchema = blog ? {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": blog.title,
    "description": blog.excerpt || blog.content.substring(0, 160),
    "image": blog.featured_image || "https://roundone.ai/images/og-image.png",
    "author": {
      "@type": "Person",
      "name": blog.author?.first_name && blog.author?.last_name
        ? `${blog.author.first_name} ${blog.author.last_name}`
        : "RoundOne Team"
    },
    "publisher": {
      "@type": "Organization",
      "name": "RoundOne AI",
      "logo": {
        "@type": "ImageObject",
        "url": "https://roundone.ai/images/dark-logo.png"
      }
    },
    "datePublished": blog.published_at || blog.created_at,
    "dateModified": blog.updated_at || blog.published_at || blog.created_at,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://roundone.ai/blog/${blog.slug}`
    }
  } : undefined;

  if (loading) {
    return (
      <>
        <Header />
        <div className="container mx-auto px-4 py-16 min-h-screen flex items-center justify-center">
          <div className="flex flex-col items-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
            <p className="text-lg text-muted-foreground">Loading blog post...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !blog) {
    return (
      <>
        <Header />
        <div className="container mx-auto px-4 py-16 min-h-screen">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl font-bold text-red-500 mb-4">Blog Post Not Found</h1>
            <p className="text-lg text-muted-foreground mb-8">
              Sorry, the blog post you're looking for doesn't exist or has been removed.
            </p>
            <Button asChild>
              <Link to="/blog">Back to Blog</Link>
            </Button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMMM dd, yyyy');
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEO 
        title={blog.title}
        description={blog.excerpt || blog.content.substring(0, 160)}
        image={blog.featured_image || "/images/og-image.png"}
        url={`/blog/${blog.slug}`}
        type="article"
        publishedTime={blog.published_at || blog.created_at}
        modifiedTime={blog.updated_at || blog.published_at || blog.created_at}
        author={blog.author?.first_name && blog.author?.last_name
          ? `${blog.author.first_name} ${blog.author.last_name}`
          : "RoundOne Team"}
        schema={articleSchema}
      />

      <Header />
      
      {/* Draft warning banner */}
      {isDraft && (
        <div className="w-full bg-amber-500 text-white py-2 px-4 text-center font-medium">
          This is a preview of a draft post. It will not be visible to the public until published.
          {useIdInsteadOfSlug && (
            <span className="ml-2 text-sm">
              <strong>Preview mode active</strong> - Viewing by ID: {extractedId}
            </span>
          )}
        </div>
      )}
      
      <main className="flex-1">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <div className="mb-8">
              <nav className="flex" aria-label="Breadcrumb">
                <ol className="inline-flex items-center space-x-1 md:space-x-3">
                  <li className="inline-flex items-center">
                    <Link to="/" className="text-sm text-muted-foreground hover:text-primary">
                      Home
                    </Link>
                  </li>
                  <li>
                    <div className="flex items-center">
                      <span className="mx-2 text-gray-400">/</span>
                      <Link to="/blog" className="text-sm text-muted-foreground hover:text-primary">
                        Blog
                      </Link>
                    </div>
                  </li>
                  <li aria-current="page">
                    <div className="flex items-center">
                      <span className="mx-2 text-gray-400">/</span>
                      <span className="text-sm text-primary font-medium truncate max-w-[200px]">
                        {blog.title}
                      </span>
                    </div>
                  </li>
                </ol>
              </nav>
            </div>

            {/* Featured Image */}
            {blog.featured_image && (
              <div className="mb-8 rounded-xl overflow-hidden">
                <img
                  src={blog.featured_image}
                  alt={blog.title}
                  className="w-full h-96 object-cover"
                />
              </div>
            )}

            {/* Title & Meta */}
            <div className="mb-8">
              <h1 className="text-4xl font-bold mb-4 flex items-center">
                {blog.title}
                {isDraft && (
                  <span className="ml-3 text-sm font-normal bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    Draft
                  </span>
                )}
              </h1>
              <div className="flex flex-wrap items-center text-sm text-muted-foreground mb-4">
                <div className="flex items-center">
                  <span>By </span>
                  <span className="font-medium ml-1">
                    {blog.author?.first_name && blog.author?.last_name
                      ? `${blog.author.first_name} ${blog.author.last_name}`
                      : 'RoundOne Team'}
                  </span>
                </div>
                <span className="mx-2">•</span>
                <time dateTime={blog.published_at || blog.created_at}>
                  {formatDate(blog.published_at || blog.created_at)}
                </time>
                
                {blog.categories && blog.categories.length > 0 && (
                  <>
                    <span className="mx-2">•</span>
                    <div className="flex flex-wrap gap-2">
                      {blog.categories.map((category: BlogCategory) => (
                        category && (
                          <Link 
                            key={category.id} 
                            to={`/blog/category/${category.slug}`}
                            className="text-xs bg-secondary px-2 py-1 rounded-full hover:bg-primary/20 transition-colors"
                          >
                            {category.name}
                          </Link>
                        )
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Excerpt */}
            {blog.excerpt && (
              <div className="mb-8">
                <p className="text-xl text-muted-foreground italic">{blog.excerpt}</p>
              </div>
            )}

            {/* Content */}
            <div className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:tracking-tight prose-p:text-gray-700 prose-p:leading-relaxed prose-p:text-lg prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-strong:text-gray-900 prose-strong:font-semibold prose-ul:list-disc prose-ol:list-decimal prose-li:my-1 prose-li:ml-4 prose-ul:pl-0 prose-ol:pl-0 prose-img:rounded-xl prose-img:shadow-md prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-gray-600">
              <div dangerouslySetInnerHTML={{ __html: blog.content }} />
            </div>
            
            {/* Share Buttons */}
            <div className="mt-12 pt-8 border-t">
              <h3 className="text-lg font-semibold mb-4">Share this post</h3>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(blog.title)}&url=${encodeURIComponent(window.location.href)}`, '_blank')}>
                  Twitter
                </Button>
                <Button variant="outline" size="sm" onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank')}>
                  LinkedIn
                </Button>
                <Button variant="outline" size="sm" onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank')}>
                  Facebook
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Add ProvenResults component */}
        <ProvenResults />
      </main>
      
      <Footer />
    </div>
  );
};

export default BlogPost; 